import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { formatPercent } from '@sohanscript/lib/utils/format';
import { Button } from '@sohanscript/ui/button';
import { Kbd, KbdGroup } from '@sohanscript/ui/kbd';
import { P } from '@sohanscript/ui/text';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
	TransformComponent,
	useControls,
	useTransformEffect,
} from 'react-zoom-pan-pinch';
import { Img } from '../../img';
import { Skeleton } from '../../skeleton';
import {
	SCALE_EPSILON,
	WHEEL_ZOOM_SENSITIVITY,
	ZOOM_MAX_FACTOR,
	ZOOM_MIN_FACTOR,
	ZOOM_STEP_FACTOR,
} from './image-inspector-shared';

export function StageFrame({ children }: { children: React.ReactNode }) {
	return (
		<div className="@container relative flex min-h-0 flex-1 touch-none select-none items-center justify-center overflow-hidden bg-background">
			{children}
		</div>
	);
}

/**
 * Placeholder that occupies the same box as the fitted image: the width is
 * the smaller of the stage width and the stage height scaled by the aspect
 * ratio, i.e. the "contain" fit the preview applies to the image itself.
 */
export function ImageStageSkeleton({
	width,
	height,
}: {
	width: number | null;
	height: number | null;
}) {
	const ratio = width && height ? width / height : null;

	return (
		<StageFrame>
			<Skeleton
				aria-hidden="true"
				className="max-h-full max-w-full"
				style={
					ratio
						? {
								aspectRatio: ratio,
								width: `min(100cqw, calc(100cqh * ${ratio}))`,
							}
						: { width: '100cqw', height: '100cqh' }
				}
			/>
		</StageFrame>
	);
}

export function ImageStage({
	url,
	alt,
	width,
	height,
}: {
	url: string;
	alt: string;
	width: number | null;
	height: number | null;
}) {
	const [hasError, setHasError] = useState(false);

	return (
		<StageFrame>
			{hasError ? (
				<div className="flex max-w-xs flex-col items-center gap-3 text-center">
					<Iconify
						icon="lucide:image-off"
						className="size-8 text-muted-foreground"
						aria-hidden="true"
					/>
					<P className="text-muted-foreground text-sm">
						{m.image_inspector_error()}
					</P>
				</div>
			) : (
				<>
					<TransformComponent
						wrapperStyle={{ width: '100%', height: '100%' }}
						contentClass="cursor-grab active:cursor-grabbing"
					>
						<Img
							src={url}
							alt={alt}
							{...(width && height ? { width, height } : {})}
							loading="eager"
							placeholder="none"
							onError={() => setHasError(true)}
							className="block h-auto w-auto"
						/>
					</TransformComponent>
					<StageFitWatcher />
					<StageWheelZoom />
				</>
			)}
		</StageFrame>
	);
}

export function StageWheelZoom() {
	const { instance, zoomToPoint } = useControls();
	const scaleRef = useRef(1);
	const pendingRef = useRef(0);
	const pointerRef = useRef({ x: 0, y: 0 });
	const frameRef = useRef<number | null>(null);

	useTransformEffect(({ state }) => {
		scaleRef.current = state.scale;
	});

	useEffect(() => {
		const wrapper = instance.wrapperComponent;
		if (!wrapper) return;

		const flush = () => {
			frameRef.current = null;
			const pending = pendingRef.current;
			pendingRef.current = 0;
			if (!pending) return;

			const factor = Math.exp(-pending * WHEEL_ZOOM_SENSITIVITY);
			const next = Math.min(Math.max(scaleRef.current * factor, 0.005), 40);
			if (next === scaleRef.current) return;

			scaleRef.current = next;
			void zoomToPoint(next, pointerRef.current.x, pointerRef.current.y, 0);
		};

		const onWheel = (event: WheelEvent) => {
			event.preventDefault();
			event.stopPropagation();

			const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1;
			pendingRef.current += event.deltaY * unit;
			pointerRef.current = { x: event.clientX, y: event.clientY };

			// NOTE: one zoom per frame keeps the gesture continuous instead of
			// jumping on every wheel event.
			if (frameRef.current === null) {
				frameRef.current = requestAnimationFrame(flush);
			}
		};

		// NOTE: { passive: false } is required for preventDefault() to work.
		wrapper.addEventListener('wheel', onWheel, { passive: false });
		return () => {
			wrapper.removeEventListener('wheel', onWheel);
			if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
		};
	}, [instance, zoomToPoint]);

	return null;
}

/**
 * Re-fits the image when the stage resizes: the detail sidebar slides in and
 * out, so the stage changes size on every frame of that animation and the
 * image has to follow it.
 */
export function StageFitWatcher() {
	const { instance, fitToView } = useControls();

	useEffect(() => {
		const wrapper = instance.wrapperComponent;
		if (!wrapper) return;

		const observer = new ResizeObserver(() => {
			void fitToView({ mode: 'contain', animationTime: 0 });
		});
		observer.observe(wrapper);
		return () => observer.disconnect();
	}, [instance, fitToView]);

	return null;
}

export function ZoomControls({
	fitScale,
	onFitScale,
}: {
	fitScale: number;
	onFitScale: React.Dispatch<React.SetStateAction<number>>;
}) {
	const { zoomIn, zoomOut, resetTransform } = useControls();
	const [scale, setScale] = useState(1);
	const measureRef = useRef({
		width: 0,
		height: 0,
		contentWidth: 0,
		contentHeight: 0,
	});

	useTransformEffect(({ state, instance }) => {
		setScale((current) =>
			Math.abs(current - state.scale) < SCALE_EPSILON ? current : state.scale,
		);

		const wrapper = instance.wrapperComponent;
		const content = instance.contentComponent;
		if (!wrapper || !content?.offsetWidth) return;

		const measured = {
			width: wrapper.clientWidth,
			height: wrapper.clientHeight,
			contentWidth: content.offsetWidth,
			contentHeight: content.offsetHeight,
		};
		const previous = measureRef.current;
		if (
			previous.width === measured.width &&
			previous.height === measured.height &&
			previous.contentWidth === measured.contentWidth &&
			previous.contentHeight === measured.contentHeight
		) {
			return;
		}
		measureRef.current = measured;

		// NOTE: the fitted view is the "contain" scale of the current content.
		const next = Math.min(
			measured.width / measured.contentWidth,
			measured.height / measured.contentHeight,
		);
		onFitScale((current) =>
			Math.abs(current - next) < 0.001 ? current : next,
		);
	});

	const minScale = fitScale * ZOOM_MIN_FACTOR;
	const maxScale = fitScale * ZOOM_MAX_FACTOR;
	const isAtMinZoom = scale <= minScale * 1.001;
	const isAtMaxZoom = scale >= maxScale * 0.999;
	const percent = formatPercent(fitScale > 0 ? (scale / fitScale) * 100 : 100);

	const zoomBy = (factor: number) => {
		const target = Math.min(Math.max(scale * factor, minScale), maxScale);
		const step = target - scale;
		if (step === 0) return;
		if (step > 0) {
			void zoomIn(step);
		} else {
			void zoomOut(-step);
		}
	};

	return (
		<div className="corner-bevel lg:scale-80 flex lg:absolute inset-x-0 w-fit justify-center mx-auto items-center gap-1 rounded-lg border border-border bg-popover">
			<Button
				type="button"
				variant="ghost"
				size="icon-sm"
				onClick={() => zoomBy(1 - ZOOM_STEP_FACTOR)}
				disabled={isAtMinZoom}
				aria-label={m.image_inspector_zoom_out()}
			>
				<Iconify icon="lucide:minus" className="size-3.5" aria-hidden="true" />
			</Button>
			<Button
				type="button"
				variant="ghost"
				size="auto"
				onClick={() => resetTransform()}
				className="min-w-12 px-1 font-mono text-xs text-muted-foreground tabular-nums"
				aria-label={m.image_inspector_reset_zoom()}
			>
				{percent}
			</Button>
			<Button
				type="button"
				variant="ghost"
				size="icon-sm"
				onClick={() => zoomBy(1 + ZOOM_STEP_FACTOR)}
				disabled={isAtMaxZoom}
				aria-label={m.image_inspector_zoom_in()}
			>
				<Iconify icon="lucide:plus" className="size-3.5" aria-hidden="true" />
			</Button>
		</div>
	);
}

function InspectorHint({
	icon,
	kbd,
	children,
}: {
	icon?: string;
	kbd?: string | string[];
	children: React.ReactNode;
}) {
	const keys = kbd ? (Array.isArray(kbd) ? kbd : [kbd]) : [];

	return (
		<span className="inline-flex items-center gap-1.5 text-muted-foreground text-sm">
			{icon ? (
				<Kbd>
					<Iconify icon={icon} className="size-3.5" aria-hidden="true" />
				</Kbd>
			) : keys.length > 1 ? (
				<KbdGroup>
					{keys.map((key) => (
						<Kbd key={key}>{key}</Kbd>
					))}
				</KbdGroup>
			) : (
				<Kbd>{keys[0]}</Kbd>
			)}
			{children}
		</span>
	);
}

export function InspectorBottomLine({
	zoomControls,
}: {
	zoomControls?: React.ReactNode;
}) {
	return (
		<div className="shrink-0 flex flex-wrap items-center gap-x-5 gap-y-1.5 relative max-lg:absolute bottom-0 lg:border-border/60 lg:border-t px-4 py-2">
			<div className="flex items-center gap-4 max-lg:hidden ">
				<InspectorHint icon="lucide:mouse">
					{m.image_inspector_hint_zoom()}
				</InspectorHint>
				<InspectorHint icon="lucide:hand">
					{m.image_inspector_hint_pan()}
				</InspectorHint>
			</div>
			{zoomControls}
			<div className="flex items-center gap-4 max-lg:hidden ml-auto">
				<InspectorHint kbd="I">
					{m.image_inspector_hint_inspector()}
				</InspectorHint>
				<InspectorHint kbd="F">
					{m.image_inspector_hint_fullscreen()}
				</InspectorHint>
				<InspectorHint kbd={['ESC', 'Q']}>
					{m.image_inspector_hint_close()}
				</InspectorHint>
			</div>
		</div>
	);
}
